// Recruiting Teams data layer — used by both the client portal (owner views
// every team in their company) and the manager portal (manager views only the
// teams they belong to via recruiting_team_managers).
//
// All queries trust RLS: the server enforces who-sees-what. We don't pre-flight
// with companies / role checks — that introduces session-hydration races
// (see STATE.md "Session-hydration races after router.replace").

import { supabase } from "./supabase";

// -----------------------------------------------------------------------------
// Shape: a recruiting team as the UI consumes it.
// -----------------------------------------------------------------------------

/** The free-form scope JSON we store on `recruiting_teams.scope`. */
export type RecruitingTeamScope = {
  schools?: string[];
  states?: string[];
  regions?: string[];
};

export type RecruitingTeam = {
  id: string;
  company_id: string;
  name: string;
  code: string | null;
  scope: RecruitingTeamScope;
  color: string;
  description: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

export type RecruitingTeamManager = {
  team_id: string;
  user_id: string;
  role: "manager" | "lead";
  added_by: string | null;
  added_at: string;
  /** Joined from user_profiles when listed in the team-managers UI. */
  full_name?: string | null;
  email?: string | null;
};

export type RecruitingTeamEvent = {
  id: string;
  team_id: string;
  title: string;
  description: string | null;
  starts_at: string;
  ends_at: string | null;
  location: string | null;
  kind: "recruiting_event" | "info_session" | "one_on_one" | "milestone" | "other";
  color: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------

/**
 * Returns every recruiting team the current user can see, scoped by RLS:
 * - Client owners: all teams in their company
 * - Managers: only the teams they belong to (recruiting_team_managers)
 * - Admins: everything
 */
export async function listMyRecruitingTeams(): Promise<RecruitingTeam[]> {
  const { data, error } = await supabase
    .from("recruiting_teams")
    .select("*")
    .order("created_at", { ascending: false });

  if (error || !data) return [];
  return (data as RecruitingTeam[]).map(normalizeScope);
}

/** Single team by id; returns null if RLS hides it or it doesn't exist. */
export async function getRecruitingTeam(id: string): Promise<RecruitingTeam | null> {
  const { data, error } = await supabase
    .from("recruiting_teams")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error || !data) return null;
  return normalizeScope(data as RecruitingTeam);
}

/** Returns the team-IDs the current user manages — mirrors my_recruiting_team_ids() server fn. */
export async function listMyManagedTeamIds(): Promise<string[]> {
  const { data, error } = await supabase
    .from("recruiting_team_managers")
    .select("team_id")
    .order("added_at", { ascending: true });

  if (error || !data) return [];
  return data.map((r) => r.team_id as string);
}

/** All managers on a given team (for the Managers tab). */
export async function listTeamManagers(teamId: string): Promise<RecruitingTeamManager[]> {
  const { data, error } = await supabase
    .from("recruiting_team_managers")
    .select("team_id, user_id, role, added_by, added_at")
    .eq("team_id", teamId)
    .order("added_at", { ascending: true });

  if (error || !data) return [];

  const userIds = data.map((r) => r.user_id as string);
  if (userIds.length === 0) return data as RecruitingTeamManager[];

  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, full_name")
    .in("id", userIds);

  const nameById = new Map<string, string | null>();
  for (const p of profiles ?? []) {
    nameById.set(p.id as string, (p as { full_name: string | null }).full_name);
  }

  return data.map((r) => ({
    ...(r as RecruitingTeamManager),
    full_name: nameById.get(r.user_id as string) ?? null,
  }));
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/** Make sure `scope` is always a well-formed object even if the DB row stored {}. */
function normalizeScope(row: RecruitingTeam): RecruitingTeam {
  const raw = (row.scope ?? {}) as Partial<RecruitingTeamScope>;
  return {
    ...row,
    scope: {
      schools: Array.isArray(raw.schools) ? raw.schools : [],
      states: Array.isArray(raw.states) ? raw.states : [],
      regions: Array.isArray(raw.regions) ? raw.regions : [],
    },
  };
}

/** True when the team has no scope filter — visible to everything in the company. */
export function isUnboundedScope(scope: RecruitingTeamScope): boolean {
  return (
    (scope.schools?.length ?? 0) === 0 &&
    (scope.states?.length ?? 0) === 0 &&
    (scope.regions?.length ?? 0) === 0
  );
}

// -----------------------------------------------------------------------------
// Writes — region CRUD for /client/company → Regions tab
// -----------------------------------------------------------------------------

export type RecruitingTeamInput = {
  name: string;
  code?: string | null;
  description?: string | null;
  scope?: RecruitingTeamScope;
  color?: string;
};

const AMBER = "#B5873A";

/** Create a region under the given company. RLS enforces that only client-role
 *  members of the company can write. Returns the new row or the error. */
export async function createRecruitingTeam(
  companyId: string,
  input: RecruitingTeamInput,
): Promise<{ team: RecruitingTeam | null; error: Error | null }> {
  const trimmedName = input.name.trim();
  if (!trimmedName) return { team: null, error: new Error("Region name is required.") };

  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id ?? null;

  const { data, error } = await supabase
    .from("recruiting_teams")
    .insert({
      company_id: companyId,
      name: trimmedName,
      code: input.code?.trim() || null,
      description: input.description?.trim() || null,
      scope: input.scope ?? {},
      color: input.color ?? AMBER,
      created_by: uid,
    })
    .select("*")
    .single();
  if (error || !data) {
    return { team: null, error: new Error(error?.message ?? "Failed to create region.") };
  }
  return { team: normalizeScope(data as RecruitingTeam), error: null };
}

export async function updateRecruitingTeam(
  teamId: string,
  patch: Partial<RecruitingTeamInput>,
): Promise<{ team: RecruitingTeam | null; error: Error | null }> {
  const update: Record<string, unknown> = {};
  if (patch.name !== undefined) {
    const t = patch.name.trim();
    if (!t) return { team: null, error: new Error("Region name is required.") };
    update.name = t;
  }
  if (patch.code !== undefined) update.code = patch.code?.trim() || null;
  if (patch.description !== undefined) update.description = patch.description?.trim() || null;
  if (patch.scope !== undefined) update.scope = patch.scope;
  if (patch.color !== undefined) update.color = patch.color;
  update.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from("recruiting_teams")
    .update(update)
    .eq("id", teamId)
    .select("*")
    .single();
  if (error || !data) {
    return { team: null, error: new Error(error?.message ?? "Failed to update region.") };
  }
  return { team: normalizeScope(data as RecruitingTeam), error: null };
}

export async function deleteRecruitingTeam(teamId: string): Promise<{ error: Error | null }> {
  const { error } = await supabase.from("recruiting_teams").delete().eq("id", teamId);
  return { error: error ? new Error(error.message) : null };
}

/** Add or remove a user's membership on a recruiting team. Used by the Members
 *  tab's region dropdown — empty target removes any existing assignment. */
export async function setUserRecruitingTeam(
  userId: string,
  teamId: string | null,
): Promise<{ error: Error | null }> {
  const { error: delErr } = await supabase
    .from("recruiting_team_managers")
    .delete()
    .eq("user_id", userId);
  if (delErr) return { error: new Error(delErr.message) };
  if (!teamId) return { error: null };

  const { data: userData } = await supabase.auth.getUser();
  const addedBy = userData?.user?.id ?? null;

  const { error } = await supabase.from("recruiting_team_managers").insert({
    team_id: teamId,
    user_id: userId,
    role: "manager",
    added_by: addedBy,
  });
  return { error: error ? new Error(error.message) : null };
}

/** Compact summary like "FSU + UF · GA" for tile labels. Empty scope returns "All". */
export function summarizeScope(scope: RecruitingTeamScope): string {
  if (isUnboundedScope(scope)) return "All";
  const parts: string[] = [];
  if (scope.schools && scope.schools.length > 0) {
    parts.push(scope.schools.join(" + "));
  }
  if (scope.states && scope.states.length > 0) {
    parts.push(scope.states.join(", "));
  }
  if (scope.regions && scope.regions.length > 0) {
    parts.push(scope.regions.join(", "));
  }
  return parts.join(" · ");
}
